import * as XLSX from 'xlsx';

export const normalizeImportHeader = (value) => String(value ?? '').trim().replace(/\s+/g, '').replace(/[（(].*$/, '');
const hiddenImportHeaders = new Set(['英文名', '英文姓名', 'staff_english_name', 'custom_line_id', 'line', 'lineid']);
const cellText = (value) => String(value ?? '').trim() === '-' ? '' : String(value ?? '').trim();

export function parsePatronWorkbook(buffer) {
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
    return workbook.SheetNames.map((name, sheetIndex) => {
        const grid = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, raw: false, defval: '', blankrows: true });
        const width = grid.reduce((max, row) => Math.max(max, row.length), 0);
        const headers = Array.from({ length: width }, (_, index) => String(grid[0]?.[index] ?? '').trim());
        const rows = grid.slice(1).map((cells, index) => ({
            id: `${sheetIndex}-${index + 2}`, rowNumber: index + 2,
            cells: Array.from({ length: width }, (_, column) => cellText(cells[column])),
        })).filter(row => row.cells.some(Boolean));
        return { id: String(sheetIndex), name, headers, rows };
    }).filter(sheet => sheet.rows.length > 0);
}

export function projectPatronImportSheets(sheets, schema) {
    return sheets.map(sheet => {
        const matches = (schema?.sheets ?? []).filter(config => config.name === sheet.name);
        const configs = matches.length === 1 ? matches : [];
        const allowed = new Set(configs.flatMap(config => config.headers).map(normalizeImportHeader));
        const columns = sheet.headers.reduce((indices, header, index) => {
            const normalized = normalizeImportHeader(header);
            if (allowed.has(normalized) && !hiddenImportHeaders.has(normalized.toLowerCase())) indices.push(index);
            return indices;
        }, []);
        const headers = columns.map(index => sheet.headers[index]);
        const present = new Set(headers.map(normalizeImportHeader));
        const unitHeaders = (configs[0]?.unit_headers ?? []).map(normalizeImportHeader);
        // Old workbooks may omit unit columns. Keep an existing single-unit alias,
        // otherwise expose the schema's unit fields so they can be edited in preview.
        const neededUnits = unitHeaders.length === 1
            ? present.has(unitHeaders[0]) || present.has('單位系所') ? [] : [allowed.has('單位系所') ? '單位系所' : unitHeaders[0]]
            : unitHeaders;
        const missingHeaders = [];
        neededUnits.forEach(unit => {
            const canonical = configs[0]?.headers.find(header => normalizeImportHeader(header) === unit);
            if (canonical && !present.has(unit)) {
                missingHeaders.push(canonical);
                present.add(unit);
            }
        });
        return { ...sheet, headers: [...headers, ...missingHeaders],
            rows: sheet.rows.map(row => ({ ...row, cells: columns.map(index => cellText(row.cells[index])) }))
                .filter(row => !columns.length || row.cells.some(Boolean))
                .map(row => ({ ...row, cells: [...row.cells, ...missingHeaders.map(() => '')] })) };
    });
}

const canonicalHeaders = (sheet, schema) => {
    const config = schema?.sheets.find(item => item.name === sheet.name);
    return sheet.headers.map(header => config?.headers.find(allowed => normalizeImportHeader(allowed) === normalizeImportHeader(header)) ?? header);
};

export function createPatronPreviewData(sheets, schema) {
    return { sheets: projectPatronImportSheets(sheets, schema).map(sheet => ({
        name: sheet.name, headers: canonicalHeaders(sheet, schema),
        rows: sheet.rows.map(row => ({ row_number: row.rowNumber, cells: row.cells })),
    })) };
}

export function normalizeImportDate(value) {
    const match = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(cellText(value));
    if (!match) return null;
    const [, year, month, day] = match.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) return null;
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function validatePatronPreview(sheets, schema) {
    const importSheets = projectPatronImportSheets(sheets, schema);
    const issues = [];
    const identifiers = new Map();
    const names = new Map();
    const add = (sheet, row, column, message) => issues.push({ sheetId: sheet.id, rowId: row?.id, rowNumber: row?.rowNumber ?? 1, column, message });
    const departments = new Map();
    (schema?.departments ?? []).forEach(({ code, id }) => {
        if (!departments.has(code)) departments.set(code, new Set());
        if (id) departments.get(code).add(String(id));
    });
    const branches = new Set((schema?.branches ?? []).map(branch => String(branch.code)));
    importSheets.forEach(sheet => {
        const configs = (schema?.sheets ?? []).filter(config => config.name === sheet.name);
        const config = configs.length === 1 ? configs[0] : null;
        if (!config) add(sheet, null, null, '工作表沒有唯一對應的讀者類型，請修正原始 Excel 的工作表名稱');
        if (names.has(sheet.name)) {
            add(sheet, null, null, '工作表名稱重複，請修正原始 Excel');
            add(names.get(sheet.name), null, null, '工作表名稱重複，請修正原始 Excel');
        } else names.set(sheet.name, sheet);
        if (config && !config.categorycode) add(sheet, null, null, '讀者類型尚未設定唯一的借閱分類，請聯絡管理員');
        const headerMap = new Map();
        sheet.headers.forEach((header, column) => {
            const normalized = normalizeImportHeader(header);
            if (!normalized) {
                if (sheet.rows.some(row => cellText(row.cells[column]))) add(sheet, null, column, '有資料的欄位缺少欄名');
                return;
            }
            if (headerMap.has(normalized)) {
                add(sheet, null, headerMap.get(normalized), `欄名重複：${normalized}`);
                add(sheet, null, column, `欄名重複：${normalized}`);
            }
            else headerMap.set(normalized, column);
            if (config && !config.headers.map(normalizeImportHeader).includes(normalized)) add(sheet, null, column, `無法辨識欄名：${normalized}`);
        });
        ['身份證號', '姓名', '所屬分館', '有效日期'].forEach(header => {
            if (!headerMap.has(header)) add(sheet, null, null, `缺少必填欄位：${header}`);
        });
        const unitHeaders = config?.unit_headers ?? [];
        if (unitHeaders.length === 1 && unitHeaders[0] !== '單位系所' && headerMap.has('單位系所') && headerMap.has(unitHeaders[0])) {
            ['單位系所', unitHeaders[0]].forEach(header => add(sheet, null, headerMap.get(header),
                `單位欄位重複：單位系所與${unitHeaders[0]}只能擇一填寫`));
        }
        const get = (row, header) => cellText(row.cells[headerMap.get(header)]);
        sheet.rows.forEach(row => {
            ['身份證號', '姓名', '所屬分館', '有效日期'].forEach(header => {
                if (!get(row, header)) add(sheet, row, headerMap.get(header), `${header}未填寫`);
            });
            const identifier = get(row, '身份證號').toUpperCase();
            if (identifier) {
                if (!identifiers.has(identifier)) identifiers.set(identifier, []);
                identifiers.get(identifier).push({ sheet, row, column: headerMap.get('身份證號') });
            }
            if (get(row, '有效日期') && !normalizeImportDate(get(row, '有效日期'))) add(sheet, row, headerMap.get('有效日期'), '有效日期格式錯誤，請填寫 YYYY-MM-DD');
            if (get(row, '所屬分館') && !branches.has(get(row, '所屬分館'))) add(sheet, row, headerMap.get('所屬分館'), '所屬分館代碼不存在');
            if (get(row, '性別') && !['1', '2'].includes(get(row, '性別'))) add(sheet, row, headerMap.get('性別'), '性別請填寫 1 或 2');
            if (get(row, 'Email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(get(row, 'Email'))) add(sheet, row, headerMap.get('Email'), 'Email 格式錯誤');
            if (config) {
                const unitHeaders = config.unit_headers ?? [];
                const unitColumns = unitHeaders.map(header => headerMap.has(header) ? header : unitHeaders.length === 1 ? '單位系所' : header);
                const supplied = [...new Set(unitColumns)].filter(header => get(row, header));
                if (!supplied.length) {
                    const missingColumns = [...new Set(unitColumns)].filter(header => headerMap.has(header));
                    if (missingColumns.length) missingColumns.forEach(header => add(sheet, row, headerMap.get(header), '單位未填寫或缺少對應欄位'));
                    else add(sheet, row, null, '單位未填寫或缺少對應欄位');
                }
                supplied.forEach(header => {
                    if (departments.get(get(row, header))?.size !== 1) add(sheet, row, headerMap.get(header), '單位代碼不存在或不唯一，請填寫完整代碼');
                });
            }
        });
    });
    identifiers.forEach(rows => {
        if (rows.length < 2) return;
        const locations = rows.slice(0, 10).map(({ sheet, row }) => `「${sheet.name}」第 ${row.rowNumber} 列`).join('、');
        rows.forEach(({ sheet, row, column }) => add(sheet, row, column, `身份證號重複：${locations}${rows.length > 10 ? `（共 ${rows.length} 列）` : ''}`));
    });
    if (!importSheets.some(sheet => sheet.rows.length)) issues.push({ message: '檔案沒有可匯入的讀者資料' });
    return issues;
}

// Server errors may only include a message and row number; locate the affected cells as well.
export function getPatronIssueColumns(sheet, issue, schema) {
    if (Number.isInteger(issue.column)) return [issue.column];
    const fields = [];
    const message = issue.message ?? '';
    if (/身份證|身分證|證號/.test(message)) fields.push('身份證號');
    if (/姓名/.test(message)) fields.push('姓名');
    if (/日期/.test(message)) fields.push('有效日期');
    if (/分館/.test(message)) fields.push('所屬分館');
    if (/Email/i.test(message)) fields.push('Email');
    if (/性別/.test(message)) fields.push('性別');
    if (/單位/.test(message)) {
        fields.push('單位系所', ...(schema?.sheets.find(config => config.name === sheet.name)?.unit_headers ?? []));
    }
    return sheet.headers.reduce((columns, header, index) => {
        if (fields.includes(normalizeImportHeader(header))) columns.push(index);
        return columns;
    }, []);
}

export function createPatronImportFile(sheets, originalName, schema) {
    const workbook = XLSX.utils.book_new();
    projectPatronImportSheets(sheets, schema).forEach(sheet => {
        const grid = [canonicalHeaders(sheet, schema)];
        sheet.rows.forEach(row => {
            grid[row.rowNumber - 1] = sheet.headers.map((header, column) => {
                const value = cellText(row.cells[column]);
                return normalizeImportHeader(header) === '有效日期' ? normalizeImportDate(value) ?? value : value;
            });
        });
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(grid), sheet.name);
    });
    return new File([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })],
        originalName.replace(/\.[^.]+$/, '') + '.xlsx',
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
